// PeekPanelHeader.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './PeekPanelHeader.css';

// Components
import IconButton from 'components/iconbutton/IconButton';
import MenuButton from 'components/menubutton/MenuButton';
import NodeButtons from 'node/buttons/NodeButtons';

/**
 * PeekPanelHeader
 *
 * Renders the header section of the PeekPanel (back, PEEK kicker, node actions, close).
 * Database / source provenance pills live in NodeMetadataFooter.
 *
 * @returns {ReactNode} - PeekPanelHeader.
 */
const PeekPanelHeader = React.memo(({
  showBackButton,
  node,
  onBackClick,
  onClose,
  hideMenuPanel = false,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- RENDER ----- //

  return (
    <div className="peek-panel-header-container">

      {/* LEFT */}
      <div
        className="peek-panel-header-left"
        style={{
          // Clearance for the mobile menu button; desktop uses a tight inset.
          paddingLeft: isMobile ? 54 : 0,
        }}
      >
        {showBackButton && (
          <IconButton
            iconKey="arrow_back"
            iconSize={26}
            color={theme.foreground}
            title="Back to previous item"
            onClick={onBackClick}
          />
        )}
        <div className="peek-panel-kicker" aria-hidden="true">
          Peek
        </div>
      </div>

      {/* ACTIONS */}
      <div className="peek-panel-actions">
        {node && !hideMenuPanel ? (
          <NodeButtons
            node={node}
            navigateOnDelete={false}
            onNodeDeleted={onClose}
          />
        ) : null}

        <MenuButton
          iconKey="close"
          color={theme.foregroundMuted}
          title="Close peek panel"
          iconSize={20}
          onClick={onClose}
        />
      </div>
    </div>
  );
});

// Dev Tools
PeekPanelHeader.displayName = 'PeekPanelHeader';

export default PeekPanelHeader;
